/**
 * Notification bell — badge + dropdown.
 *
 * Talks to nexus-core (/api/notification/*), never to nexus-messaging.
 * nexus-core takes the recipient from the validated token, so there is no
 * address to pass from here and no way for this component to ask about
 * anyone but the signed-in user.
 *
 * The body of every notification is PLAIN TEXT and is rendered as text.
 * app_card v1 deliberately carries no markup and the engine emits no
 * markdown markers, so there is nothing here that should ever become
 * dangerouslySetInnerHTML — that is the whole reason the body is plain.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { apiFetch, apiPost } from "../shared/api/client";
import { T } from "../shared/styles/theme";

const POLL_MS = 45000;

const SEVERITY = {
  info: { dot: "#3b82f6", label: "Info" },
  success: { dot: "#16a34a", label: "Success" },
  warning: { dot: "#d97706", label: "Warning" },
  error: { dot: "#dc2626", label: "Error" },
};

/**
 * Parse a server timestamp as UTC when it does not say otherwise.
 *
 * The API reads Azure SQL DATETIME2, which stores no timezone, so a timestamp
 * can arrive as "2026-08-27T07:15:00" — no Z, no offset. The JS spec says a
 * date-time string WITHOUT an offset is LOCAL time, so a browser in IST read
 * 07:15 UTC as 07:15 IST and showed a notification that had just landed as
 * "5h ago" — precisely the 5:30 offset.
 *
 * nexus-messaging now stamps the Z itself. This stays because it makes the two
 * deploys order-independent: zoned input is passed through untouched, so a new
 * client against an old API and an old client against a new API are both right.
 */
function parseUtc(value) {
  if (!value) return NaN;
  if (typeof value !== "string") return new Date(value).getTime();
  // Date-only forms ("2026-08-27") are ALREADY parsed as UTC by the spec, and
  // appending Z to one produces an invalid date. Only a time part is at risk.
  const hasTime = value.includes("T") || value.includes(" ");
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const s = hasTime && !hasZone ? `${value.replace(" ", "T")}Z` : value;
  return new Date(s).getTime();
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = parseUtc(iso);
  if (Number.isNaN(then)) return "";
  // Clamp: a caller-supplied event time can legitimately be in the future
  // (a scheduled run, a clock skew). Math.floor on a negative would otherwise
  // produce "-3m ago".
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(parseUtc(iso)).toLocaleDateString();
}

/**
 * The list is ordered newest-ARRIVED first (the API sorts by delivery id), so
 * the time shown next to each row must be the ARRIVAL time too. Showing
 * event_timestamp here instead — which is when the underlying thing happened,
 * and which a caller may set to anything, including the future — made a
 * correctly ordered list read as scrambled: a row that arrived a minute ago
 * could display "18h ago" and sit above one showing "2h ago".
 *
 * The event time is still useful (a run that finished at 02:00 and was
 * notified at 09:00 should say so), so it is shown as secondary context when
 * it differs from arrival by more than a few minutes.
 */
const EVENT_GAP_MS = 5 * 60 * 1000;

function whenLabel(n) {
  const arrived = n.created_at;
  const happened = n.event_timestamp;
  const base = timeAgo(arrived);
  if (!happened || !arrived) return base;
  const a = parseUtc(arrived);
  const h = parseUtc(happened);
  if (Number.isNaN(a) || Number.isNaN(h) || Math.abs(a - h) < EVENT_GAP_MS) return base;
  const t = new Date(h);
  const hhmm = `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
  const sameDay = t.toDateString() === new Date(a).toDateString();
  return `${base} · occurred ${sameDay ? hhmm : t.toLocaleDateString()}`;
}

export default function NotificationBell({ isMobile }) {
  const { instance, inProgress, accounts } = useMsal();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  const ready =
    inProgress === InteractionStatus.None && accounts && accounts.length > 0;

  const loadCount = useCallback(async () => {
    if (!ready) return;
    try {
      const d = await apiFetch(instance, "/api/notification/count");
      setUnread(d?.unread ?? 0);
    } catch {
      // A failing badge must not surface as an error on every page — the
      // backend already answers 200/degraded when the engine is down, so
      // reaching here means the portal API itself is unhappy. Stay quiet.
      setUnread(0);
    }
  }, [instance, ready]);

  const loadList = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setError(null);
    try {
      // Filtering server-side, not in the browser: the client only ever holds
      // one page, so hiding read rows locally would show "5 of 20" and call it
      // the unread list.
      const qs = unreadOnly ? "?limit=20&unread_only=1" : "?limit=20";
      const d = await apiFetch(instance, `/api/notification/inbox${qs}`);
      setItems(d?.notifications || []);
      setNextBeforeId(d?.next_before_id ?? null);
    } catch (e) {
      setError(e?.message || "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, [instance, ready, unreadOnly]);

  // Badge poll. Every open tab runs this, so the endpoint behind it is a
  // single filtered-index count and nothing more.
  useEffect(() => {
    if (!ready) return;
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [ready, loadCount]);

  // Keyset paging, not offset: the inbox gains rows at the top while you
  // scroll, and an offset would show the same row twice.
  const loadMore = useCallback(async () => {
    if (!ready || loadingMore || !nextBeforeId) return;
    setLoadingMore(true);
    try {
      const qs = unreadOnly
        ? `?limit=20&unread_only=1&before_id=${nextBeforeId}`
        : `?limit=20&before_id=${nextBeforeId}`;
      const d = await apiFetch(instance, `/api/notification/inbox${qs}`);
      const page = d?.notifications || [];
      // Guard against a duplicate page: appending blind would give React two
      // rows with the same key and the list would flicker.
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.delivery_id));
        return [...prev, ...page.filter((x) => !seen.has(x.delivery_id))];
      });
      setNextBeforeId(d?.next_before_id ?? null);
    } catch {
      /* leave the cursor; the next scroll retries */
    } finally {
      setLoadingMore(false);
    }
  }, [instance, ready, loadingMore, nextBeforeId, unreadOnly]);

  function onScroll(e) {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) loadMore();
  }

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Reading an item while the Unread filter is on should remove it from view —
  // otherwise the list contradicts its own heading.
  useEffect(() => {
    if (unreadOnly) setItems((prev) => prev.filter((x) => !x.is_read));
  }, [unreadOnly, unread]);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function markRead(n, e) {
    e?.stopPropagation();
    // Optimistic: the click should feel instant. Reconciled by loadCount().
    setItems((prev) =>
      prev.map((x) =>
        x.notification_id === n.notification_id ? { ...x, is_read: true, state: "read" } : x
      )
    );
    setUnread((u) => Math.max(0, u - (n.is_read ? 0 : 1)));
    try {
      await apiPost(instance, "/api/notification/read", {
        notification_id: n.notification_id,
        is_read: true,
      });
    } catch {
      /* reconciled on the next poll */
    }
    loadCount();
  }

  async function dismiss(n, e) {
    e?.stopPropagation();
    setItems((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
    setUnread((u) => Math.max(0, u - (n.is_read ? 0 : 1)));
    try {
      await apiPost(instance, "/api/notification/dismiss", {
        notification_id: n.notification_id,
      });
    } catch {
      loadList();
    }
    loadCount();
  }

  async function dismissAll() {
    setItems([]);
    setNextBeforeId(null);
    setUnread(0);
    try {
      await apiPost(instance, "/api/notification/dismiss-all", {});
    } catch {
      loadList();
    }
    loadCount();
  }

  async function markAllRead() {
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true, state: "read" })));
    setUnread(0);
    try {
      await apiPost(instance, "/api/notification/read-all", {});
    } catch {
      loadList();
    }
    loadCount();
  }

  function openItem(n) {
    if (!n.is_read) markRead(n);
    if (n.url) {
      setOpen(false);
      // Portal-relative deep links only. An absolute URL from a producer is
      // not followed by the router — it would be an open-redirect surface.
      if (n.url.startsWith("/")) navigate(n.url);
      else window.open(n.url, "_blank", "noopener,noreferrer");
    }
  }

  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        onClick={() => setOpen((v) => !v)}
        title={unread ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "Notifications"}
        style={{
          width: 38,
          height: 38,
          minWidth: 38,
          borderRadius: 8,
          background: open ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          transition: "background 0.15s",
        }}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
             stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: 3,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 8,
              background: "#dc2626",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {badge}
          </span>
        )}
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            width: isMobile ? "calc(100vw - 24px)" : 380,
            maxWidth: 380,
            maxHeight: 460,
            display: "flex",
            flexDirection: "column",
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: "0 10px 30px rgba(0,0,0,0.16)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 13 }}>Notifications</strong>
              <div
                role="tablist"
                aria-label="Filter notifications"
                style={{
                  display: "flex",
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                {[
                  { key: false, label: "All" },
                  { key: true, label: unread > 0 ? `Unread (${unread})` : "Unread" },
                ].map((t) => {
                  const active = unreadOnly === t.key;
                  return (
                    <button
                      key={String(t.key)}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setUnreadOnly(t.key)}
                      style={{
                        border: "none",
                        cursor: "pointer",
                        padding: "3px 9px",
                        fontSize: 11,
                        fontWeight: active ? 650 : 500,
                        fontVariantNumeric: "tabular-nums",
                        background: active ? (T.accent || "#1a3a5c") : "transparent",
                        color: active ? "#ffffff" : (T.textMuted || "#586069"),
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  style={{
                    border: "none", background: "none",
                    color: T.accent || "#1a3a5c",
                    fontSize: 11.5, cursor: "pointer", padding: 0,
                  }}
                >
                  Mark all as read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={dismissAll}
                  title="Hide every notification from the bell"
                  style={{
                    border: "none", background: "none",
                    color: T.textMuted || "#586069",
                    fontSize: 11.5, cursor: "pointer", padding: 0,
                  }}
                >
                  Dismiss all
                </button>
              )}
            </div>
          </div>

          <div style={{ overflowY: "auto" }} onScroll={onScroll}>
            {loading && (
              <div style={{ padding: 18, fontSize: 12.5, color: T.textMuted || "#586069" }}>
                Loading…
              </div>
            )}
            {!loading && error && (
              <div style={{ padding: 18, fontSize: 12.5, color: "#dc2626" }}>{error}</div>
            )}
            {!loading && !error && items.length === 0 && (
              <div style={{ padding: 24, fontSize: 12.5, color: T.textMuted || "#586069", textAlign: "center" }}>
                {unreadOnly ? "Nothing unread." : "Nothing here yet."}
              </div>
            )}
            {!loading &&
              !error &&
              items.map((n) => {
                const sev = SEVERITY[n.severity] || SEVERITY.info;
                return (
                  <div
                    key={n.delivery_id}
                    onClick={() => openItem(n)}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "11px 14px",
                      borderBottom: `1px solid ${T.border}`,
                      cursor: n.url ? "pointer" : "default",
                      background: n.is_read ? "transparent" : "rgba(59,130,246,0.06)",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        marginTop: 5,
                        borderRadius: 4,
                        flexShrink: 0,
                        background: n.is_read ? "transparent" : sev.dot,
                        border: n.is_read ? `1px solid ${T.border}` : "none",
                      }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: n.is_read ? 500 : 650, marginBottom: 2 }}>
                        {n.title}
                      </div>
                      {/* Plain text, rendered as text. See the file header.
                          Rendered ONLY when there is a body — a title-only
                          notification should be a compact card, not one with
                          an empty line reserved under it. */}
                      {(n.card?.body || "").trim() && (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: T.textMuted || "#586069",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {n.card.body}
                        </div>
                      )}
                      <div style={{ fontSize: 10.5, color: T.textMuted || "#8b949e", marginTop: 4 }}>
                        {(n.source_display || n.source)
                          ? `${n.source_display || n.source} · `
                          : ""}
                        {whenLabel(n)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => dismiss(n, e)}
                      title="Dismiss"
                      style={{
                        border: "none",
                        background: "none",
                        color: T.textMuted || "#8b949e",
                        cursor: "pointer",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                        alignSelf: "flex-start",
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            {loadingMore && (
              <div style={{ padding: 10, fontSize: 11.5, textAlign: "center",
                            color: T.textMuted || "#586069" }}>
                Loading more…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
